import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { Ban, BookOpen, Coins, Lock, MailCheck, MailWarning, RotateCcw, ScrollText, ShieldCheck, ShieldOff, SlidersHorizontal, TriangleAlert, UserRound, Wallet } from 'lucide-react'

import { adjustUserBalance, getUserDetail, getUserLedger, type AdminLedgerEntry, type AdminUser } from '../api/admin'
import {
  Avatar,
  Badge,
  Button,
  Callout,
  Card,
  CountUp,
  Drawer,
  EmptyState,
  Field,
  IdChip,
  Input,
  KeyValue,
  Pager,
  SegmentedControl,
  Select,
  Skeleton,
  SkeletonRows,
  SkeletonText,
  StatCard,
  StatusPill,
  Table,
  Tabs,
  Textarea,
  cx,
  describeError,
  money,
  statusLabel,
  tableClass,
  toNumber,
  useToast,
  useTotp,
  when,
  type TabOption,
} from '../components/ui/Ui'
import styles from './UserDetail.module.css'

const ENTRY_RU: Record<string, string> = {
  deposit: 'депозит', withdrawal: 'вывод', withdrawal_fee: 'комиссия сети', withdrawal_platform_fee: 'комиссия платформы за вывод', withdrawal_lock: 'резерв вывода', withdrawal_release: 'возврат резерва',
  trade_buy: 'покупка', trade_sell: 'продажа', trading_fee: 'комиссия', realized_pnl: 'результат', funding: 'фандинг',
  margin_lock: 'маржа', margin_release: 'возврат маржи', order_lock: 'резерв ордера', order_release: 'возврат ордера', liquidation: 'ликвидация',
  transfer_in: 'перевод +', transfer_out: 'перевод −', copy_allocation: 'в копирование', copy_release: 'из копирования', copy_commission: 'комиссия мастера',
  referral: 'партнёрка', bonus: 'промо-бонус', adjustment: 'корректировка',
}

type AccountType = 'spot' | 'futures' | 'copy'

/** Sub-accounts in the panel's language: the ledger stores the codes. */
export const ACCOUNT_RU: Record<AccountType, string> = { spot: 'спот', futures: 'фьючерсы', copy: 'копирование' }
const ACCOUNT_TO: Record<AccountType, string> = { spot: 'на спот', futures: 'на фьючерсный субсчёт', copy: 'на субсчёт копирования' }
const ACCOUNT_FROM: Record<AccountType, string> = { spot: 'со спота', futures: 'с фьючерсного субсчёта', copy: 'с субсчёта копирования' }

function accountName(code: string): string {
  return ACCOUNT_RU[code as AccountType] ?? code
}

function newIdempotencyKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? `adj-${crypto.randomUUID()}` : `adj-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

/**
 * The signed amount as typed or pasted: a real minus (U+2212, the one the
 * hint shows), an en or em dash and a decimal comma all mean what the
 * operator meant. Losing the sign would turn a debit into a credit.
 */
export function normalizeSignedAmount(raw: string): string {
  const unified = raw.replace(/[−‒–—﹣－]/g, '-').replace(/,/g, '.').replace(/[^0-9.-]/g, '')
  const negative = unified.trimStart().startsWith('-')
  const digits = unified.replace(/-/g, '')
  const [whole = '', ...rest] = digits.split('.')
  const number = rest.length > 0 ? `${whole}.${rest.join('')}` : whole
  return negative ? `-${number}` : number
}

export interface AccessChange {
  row: AdminUser
  status?: 'active' | 'suspended' | 'banned'
  role?: 'user' | 'admin' | 'support' | 'finance' | 'content'
}

const ROLE_RU: Record<NonNullable<AccessChange['role']>, string> = { user: 'пользователь', admin: 'админ', support: 'поддержка', finance: 'финансы', content: 'контент' }

type Section = 'overview' | 'ledger' | 'adjust'
type LedgerFilter = 'all' | AccountType

const LEDGER_FILTERS: TabOption<LedgerFilter>[] = [
  { value: 'all', label: 'Все субсчета' },
  { value: 'spot', label: 'Спот' },
  { value: 'futures', label: 'Фьючерсы' },
  { value: 'copy', label: 'Копирование' },
]

function plural(count: number, one: string, few: string, many: string): string {
  const tail = count % 100
  if (tail >= 11 && tail <= 14) return many
  if (count % 10 === 1) return one
  if (count % 10 >= 2 && count % 10 <= 4) return few
  return many
}

/** Sum of decimal strings, for a display figure (two decimals is all it shows). */
function sum(values: readonly string[]): string {
  return values.reduce((total, value) => total + (toNumber(value) ?? 0), 0).toFixed(2)
}

/** A staff desk reads in the info tone; a client stays neutral. */
function RolePill({ role }: { role: string }): JSX.Element {
  return (
    <StatusPill status={role} tone={role === 'user' ? 'neutral' : 'info'}>
      {role === 'user' ? 'клиент' : `роль: ${statusLabel(role)}`}
    </StatusPill>
  )
}

/** An amount with its sign in tone: credits in the accent, debits in red. */
function Signed({ value }: { value: string }): JSX.Element {
  const n = toNumber(value) ?? 0
  return <span className={n > 0 ? styles.credit : n < 0 ? styles.debit : undefined}>{n > 0 ? '+' : ''}{money(value, 2)}</span>
}

/**
 * One person's money as the ledger holds it: balances by sub-account, every
 * entry with the balance after it, and a correction that is itself an entry
 * with a reason — never an edit of a balance. The access block (status and
 * desk) is shown to the desks that may change it.
 */
export function UserDetail({
  userId,
  open = true,
  canAdjust,
  canManage = false,
  canChangeRole = false,
  onAccessChange,
  onClose,
}: {
  userId: string
  /** The drawer slides out when false; the parent keeps it mounted for the exit. */
  open?: boolean
  canAdjust: boolean
  canManage?: boolean
  canChangeRole?: boolean
  onAccessChange?: (change: AccessChange) => void
  onClose: () => void
}): JSX.Element {
  const toast = useToast()
  const withTotp = useTotp()
  const queryClient = useQueryClient()
  const [section, setSection] = useState<Section>('overview')
  const [page, setPage] = useState(1)
  const [account, setAccount] = useState<'' | AccountType>('')
  const [form, setForm] = useState({ assetSymbol: 'USDT', accountType: 'spot' as AccountType, amount: '', reason: '' })
  // One key per filled-in correction: a retry of the same form after a lost answer replays; any edit starts a new one.
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey)
  const edit = (patch: Partial<typeof form>): void => {
    setForm((current) => ({ ...current, ...patch }))
    setIdempotencyKey(newIdempotencyKey())
  }

  const detail = useQuery({ queryKey: ['user-detail', userId], queryFn: () => getUserDetail(userId) })
  const ledger = useQuery({ queryKey: ['user-ledger', userId, page, account], queryFn: () => getUserLedger(userId, page, account === '' ? undefined : account) })

  const adjust = useMutation({
    mutationFn: (code: string) => adjustUserBalance(userId, { ...form, amount: form.amount, reason: form.reason.trim() }, code, idempotencyKey),
    onSuccess: (result) => {
      toast(
        `${result.replayed ? 'Эта корректировка уже была проведена' : 'Корректировка проведена'}: ${money(result.entry.amount)} ${result.entry.assetSymbol}, ${accountName(result.entry.accountType)}; доступно ${money(result.balance.available)}`,
      )
      setForm((current) => ({ ...current, amount: '', reason: '' }))
      setIdempotencyKey(newIdempotencyKey())
      void queryClient.invalidateQueries({ queryKey: ['user-detail', userId] })
      void queryClient.invalidateQueries({ queryKey: ['user-ledger', userId] })
      void queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (error) => toast(describeError(error), true),
  })

  const user = detail.data?.user
  const balances = detail.data?.balances ?? []
  const reasonOf = (entry: AdminLedgerEntry): string => {
    const reason = entry.metadata?.['reason']
    return typeof reason === 'string' ? reason : ''
  }
  const amount = Number(form.amount)
  const amountValid = form.amount !== '' && form.amount !== '-' && Number.isFinite(amount) && amount !== 0
  const symbol = form.assetSymbol.trim() || 'актив'
  const adjustLabel = !amountValid
    ? 'Провести корректировку'
    : amount < 0
      ? `Списать ${money(String(-amount))} ${symbol} ${ACCOUNT_FROM[form.accountType]}`
      : `Зачислить ${money(String(amount))} ${symbol} ${ACCOUNT_TO[form.accountType]}`
  const target = balances.find((balance) => balance.accountType === form.accountType && balance.assetSymbol === form.assetSymbol.trim())

  const usdt = balances.filter((balance) => balance.assetSymbol === 'USDT')
  const showAccess = Boolean(user && onAccessChange && user.role !== 'master' && (canManage || canChangeRole))

  const sections: TabOption<Section>[] = [
    { value: 'overview', label: 'Обзор', icon: <UserRound /> },
    { value: 'ledger', label: 'Леджер', icon: <ScrollText />, count: ledger.data?.total },
    ...(canAdjust ? [{ value: 'adjust' as const, label: 'Корректировка', icon: <SlidersHorizontal /> }] : []),
  ]

  let body: ReactNode
  if (section === 'overview') {
    body = (
      <>
        <div className={styles.stats}>
          <StatCard label="Доступно, USDT" value={detail.isSuccess ? sum(usdt.map((balance) => balance.available)) : null} decimals={2} icon={<Wallet />} loading={detail.isPending} hint="все субсчета" />
          <StatCard label="В резерве, USDT" value={detail.isSuccess ? sum(usdt.map((balance) => balance.locked)) : null} decimals={2} icon={<Lock />} tone="warning" loading={detail.isPending} hint="ордера, маржа, выводы" />
          <StatCard
            label="Проводок"
            value={ledger.data?.total ?? null}
            icon={<BookOpen />}
            tone="info"
            loading={ledger.isPending}
            hint={account === '' ? 'в леджере' : `субсчёт: ${accountName(account)}`}
          />
        </div>

        <Card title="Балансы" icon={<Coins />} description="По субсчетам и активам, как их держит леджер" flush>
          {detail.isPending ? (
            <Table stickyHeader={false} stagger={false} busy ariaLabel="Загрузка балансов">
              <tbody>
                <SkeletonRows rows={2} columns={4} />
              </tbody>
            </Table>
          ) : balances.length === 0 ? (
            <EmptyState compact icon={<Wallet />} title="Балансов нет" description="Пользователь ещё не пополнял счёт." />
          ) : (
            <Table stickyHeader={false} ariaLabel="Балансы">
              <thead>
                <tr>
                  <th>Субсчёт</th>
                  <th>Актив</th>
                  <th className={tableClass.right}>Доступно</th>
                  <th className={tableClass.right}>Заблокировано</th>
                </tr>
              </thead>
              <tbody>
                {balances.map((balance) => (
                  <tr key={`${balance.accountType}-${balance.assetSymbol}`}>
                    <td>{accountName(balance.accountType)}</td>
                    <td>
                      <span className={styles.asset}>{balance.assetSymbol}</span>
                    </td>
                    <td className={tableClass.num}>{money(balance.available, 2)}</td>
                    <td className={cx(tableClass.num, (toNumber(balance.locked) ?? 0) === 0 ? tableClass.muted : '')}>{money(balance.locked, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        <Card title="Аккаунт" icon={<ShieldCheck />} description="Доступ, верификация, безопасность и партнёрская программа">
          {user ? (
            <KeyValue
              columns={2}
              labelWidth={128}
              items={[
                { label: 'Роль', value: ROLE_RU[user.role as keyof typeof ROLE_RU] ?? statusLabel(user.role) },
                { label: 'Статус', value: <StatusPill status={user.status} /> },
                { label: 'Верификация', value: user.kycStatus ? <StatusPill status={user.kycStatus} /> : <span className={styles.muted}>не подавал</span> },
                {
                  label: 'Почта',
                  value: user.emailVerified ? (
                    <span className={styles.flag}>
                      <MailCheck aria-hidden="true" className={styles.good} />
                      подтверждена
                    </span>
                  ) : (
                    <span className={styles.flag}>
                      <MailWarning aria-hidden="true" className={styles.warn} />
                      не подтверждена
                    </span>
                  ),
                },
                {
                  label: '2FA',
                  value: user.totpEnabled ? (
                    <span className={styles.flag}>
                      <ShieldCheck aria-hidden="true" className={styles.good} />
                      включена
                    </span>
                  ) : (
                    <span className={styles.flag}>
                      <ShieldOff aria-hidden="true" className={styles.warn} />
                      выключена
                    </span>
                  ),
                },
                { label: 'Последний вход', value: user.lastLoginAt ? when(user.lastLoginAt) : <span className={styles.muted}>не входил</span>, mono: user.lastLoginAt !== null },
                { label: 'Регистрация', value: when(user.createdAt), mono: true },
                { label: 'Обновлён', value: when(user.updatedAt), mono: true },
                { label: 'Реф. код', value: user.referralCode ? <IdChip value={user.referralCode} full label="реферальный код" /> : <span className={styles.muted}>нет</span> },
                { label: 'Пригласил', value: user.referredBy ? <IdChip value={user.referredBy} label="id пригласившего" /> : <span className={styles.muted}>пришёл сам</span> },
              ]}
            />
          ) : (
            <SkeletonText lines={4} />
          )}
        </Card>
      </>
    )
  } else if (section === 'ledger') {
    body = (
      <>
        <div className={styles.ledgerBar}>
          <SegmentedControl
            size="sm"
            ariaLabel="Субсчёт"
            value={account === '' ? 'all' : account}
            options={LEDGER_FILTERS}
            onChange={(next) => {
              setAccount(next === 'all' ? '' : next)
              setPage(1)
            }}
          />
          <span className={styles.muted}>{ledger.data ? `${ledger.data.total} ${plural(ledger.data.total, 'проводка', 'проводки', 'проводок')}` : ''}</span>
        </div>
        <Card flush>
          {ledger.isError ? (
            <EmptyState compact tone="error" icon={<TriangleAlert />} title="Леджер не загрузился" description={describeError(ledger.error)} action={<Button size="sm" onClick={() => void ledger.refetch()}>Повторить</Button>} />
          ) : ledger.isSuccess && ledger.data.items.length === 0 ? (
            <EmptyState compact icon={<ScrollText />} title="Проводок нет" description={account === '' ? 'Движений по счёту ещё не было.' : `На субсчёте «${accountName(account)}» движений не было.`} />
          ) : (
            <Table stickyHeader={false} ariaLabel="Леджер" busy={ledger.isPending} key={`${account}-${page}`}>
              <thead>
                <tr>
                  <th>Когда</th>
                  <th>Тип</th>
                  <th>Субсчёт</th>
                  <th className={tableClass.right}>Сумма</th>
                  <th className={tableClass.right}>Остаток после</th>
                  <th>Основание</th>
                </tr>
              </thead>
              <tbody>
                {ledger.isPending ? (
                  <SkeletonRows rows={6} columns={6} />
                ) : (
                  (ledger.data?.items ?? []).map((entry) => (
                    <tr key={entry.id}>
                      <td className={cx(tableClass.muted, 'num')}>{when(entry.createdAt)}</td>
                      <td>{ENTRY_RU[entry.entryType] ?? entry.entryType}</td>
                      <td className={tableClass.muted}>{accountName(entry.accountType)}</td>
                      <td className={tableClass.num}>
                        <Signed value={entry.amount} /> <span className={styles.unit}>{entry.assetSymbol}</span>
                      </td>
                      <td className={tableClass.num}>
                        {money(entry.balanceAfter, 2)} <span className={styles.unit}>{entry.assetSymbol}</span>
                      </td>
                      <td className={cx(tableClass.wrap, tableClass.muted)}>{reasonOf(entry) || `${entry.refType ?? ''} ${entry.refId ? entry.refId.slice(0, 12) : ''}`.trim() || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
          {ledger.data ? <Pager page={page} pageSize={ledger.data.pageSize} total={ledger.data.total} onPage={setPage} /> : null}
        </Card>
      </>
    )
  } else {
    body = (
      <>
        <Callout tone="info" title="Корректировка — отдельная проводка">
          Проводка «корректировка» с причиной и вашим именем в аудите; баланс не редактируется напрямую. Списание не может увести доступный остаток ниже нуля.
        </Callout>
        <Card
          title="Новая корректировка"
          icon={<SlidersHorizontal />}
          description="Подтверждается кодом 2FA"
          footer={
            <div className={styles.adjustFoot}>
              <Button
                variant={amountValid && amount < 0 ? 'danger' : 'primary'}
                loading={adjust.isPending}
                disabled={!amountValid || form.reason.trim().length < 3 || form.assetSymbol.trim().length < 2}
                onClick={() => void withTotp((code) => adjust.mutateAsync(code))}
              >
                {adjustLabel}
              </Button>
            </div>
          }
        >
          <div className={styles.adjustGrid}>
            <Field label="Актив">
              <Input mono value={form.assetSymbol} onChange={(event) => edit({ assetSymbol: event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })} />
            </Field>
            <Field label="Субсчёт">
              <Select value={form.accountType} onChange={(event) => edit({ accountType: event.target.value as AccountType })}>
                <option value="spot">спот</option>
                <option value="futures">фьючерсы</option>
                <option value="copy">копирование</option>
              </Select>
            </Field>
            <Field label="Сумма (со знаком)" hint="−25 списывает, 25 зачисляет" aside={target ? `доступно ${money(target.available, 2)}` : undefined}>
              <Input mono inputMode="decimal" value={form.amount} trailing={form.assetSymbol.trim() || undefined} onChange={(event) => edit({ amount: normalizeSignedAmount(event.target.value) })} />
            </Field>
          </div>
          <Field label="Причина" hint="Не короче трёх символов; останется в леджере и в аудите.">
            <Textarea value={form.reason} onChange={(event) => edit({ reason: event.target.value })} />
          </Field>
        </Card>
      </>
    )
  }

  const accessActions =
    user && showAccess && onAccessChange ? (
      <div className={styles.access}>
        {canChangeRole ? (
          <Select
            compact
            value={user.role}
            aria-label="Роль"
            title="Роль (стол) в панели"
            className={styles.roleSelect}
            onChange={(event) => {
              const role = event.target.value as NonNullable<AccessChange['role']>
              if (role !== user.role) onAccessChange({ row: user as AdminUser, role })
            }}
          >
            {(Object.keys(ROLE_RU) as Array<NonNullable<AccessChange['role']>>).map((role) => (
              <option key={role} value={role}>
                {ROLE_RU[role]}
              </option>
            ))}
          </Select>
        ) : null}
        {canManage ? (
          user.status !== 'active' ? (
            <Button size="sm" icon={<RotateCcw />} onClick={() => onAccessChange({ row: user as AdminUser, status: 'active' })}>
              Восстановить доступ
            </Button>
          ) : (
            <Button size="sm" variant="danger" onClick={() => onAccessChange({ row: user as AdminUser, status: 'suspended' })}>
              Приостановить
            </Button>
          )
        ) : null}
        {canManage && user.status !== 'banned' ? (
          <Button size="sm" variant="danger" icon={<Ban />} onClick={() => onAccessChange({ row: user as AdminUser, status: 'banned' })}>
            Заблокировать
          </Button>
        ) : null}
      </div>
    ) : null

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={960}
      title="Карточка пользователя"
      subtitle={user ? `Регистрация ${when(user.createdAt)} · ${user.lastLoginAt ? `вход ${when(user.lastLoginAt)}` : 'не входил'}` : undefined}
      actions={accessActions}
    >
      {detail.isError ? (
        <EmptyState tone="error" icon={<TriangleAlert />} title="Карточка не загрузилась" description={describeError(detail.error)} action={<Button onClick={() => void detail.refetch()}>Повторить</Button>} />
      ) : (
        <>
          <section className={styles.profile} aria-label="Профиль">
            {user ? <Avatar name={user.email} size={56} /> : <Skeleton circle width={56} height={56} />}
            <div className={styles.identity}>
              {user ? (
                <>
                  <div className={styles.emailRow}>
                    <span className={styles.email} title={user.email}>
                      {user.email}
                    </span>
                    <IdChip value={user.id} label="id пользователя" />
                  </div>
                  <div className={styles.pills}>
                    {user.role !== 'user' ? <RolePill role={user.role} /> : null}
                    <StatusPill status={user.status} />
                    {user.kycStatus ? <StatusPill status={user.kycStatus}>KYC: {statusLabel(user.kycStatus)}</StatusPill> : <Badge tone="neutral">KYC не подавал</Badge>}
                    <Badge tone={user.totpEnabled ? 'accent' : 'warning'} icon={user.totpEnabled ? <ShieldCheck /> : <ShieldOff />}>
                      {user.totpEnabled ? '2FA включена' : '2FA выключена'}
                    </Badge>
                    {user.emailVerified ? null : (
                      <Badge tone="warning" icon={<MailWarning />}>
                        почта не подтверждена
                      </Badge>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Skeleton width={260} height={18} />
                  <Skeleton width={320} height={22} radius={999} />
                </>
              )}
            </div>
            {user ? (
              <div className={styles.spot}>
                <span className={styles.spotLabel}>Спот, USDT</span>
                <CountUp value={user.spotUsdt} decimals={2} className={styles.spotValue} />
              </div>
            ) : null}
          </section>

          <Tabs variant="underline" ariaLabel="Разделы карточки" value={section} options={sections} onChange={setSection} className={styles.tabs} />

          <div className={styles.section} key={section} role="tabpanel" aria-label={sections.find((option) => option.value === section)?.label as string}>
            {body}
          </div>
        </>
      )}
    </Drawer>
  )
}
